import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Rocket, Pause, Square, Send } from 'lucide-react';
import { api } from '../api';
import { useLoad } from '../hooks/useLoad';
import { useOrg } from '../auth/OrgContext';
import { Card, PageHeader, Badge, statusTone, EmptyState, cx } from '../components/ui';
import { ConfigurationSection } from './ConfigurationSection';

interface Campaign {
  id: string;
  status: string;
  recipient_count: number;
  org_id: string;
}
interface Named {
  id: string;
  name: string;
}

export function CampaignsPage() {
  const { withOrg, selectedOrgId } = useOrg();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'config' ? 'config' : 'campaigns';
  const campaigns = useLoad(
    () => api.get<{ data: Campaign[] }>(withOrg('/v1/campaigns')),
    [selectedOrgId],
  );
  const orgs = useLoad(() => api.get<{ data: Named[] }>('/v1/orgs'));
  const templates = useLoad(() => api.get<{ data: Named[] }>('/v1/templates'));
  const senders = useLoad(() =>
    api.get<{ data: { id: string; display_name: string }[] }>('/v1/sender-profiles'),
  );

  const [orgId, setOrgId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [senderId, setSenderId] = useState('');
  const [listId, setListId] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const lists = useLoad(
    () =>
      orgId
        ? api.get<{ data: Named[] }>(`/v1/orgs/${orgId}/target-lists`)
        : Promise.resolve({ data: [] }),
    [orgId],
  );

  async function act(id: string, action: string) {
    setMsg(null);
    try {
      await api.post(`/v1/campaigns/${id}/${action}`, {});
      campaigns.reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Action failed');
    }
  }

  async function create() {
    setMsg(null);
    try {
      await api.post('/v1/campaigns', {
        org_id: orgId,
        template_id: templateId,
        sender_profile_id: senderId,
        target_list_id: listId,
      });
      setMsg('Campaign created (draft).');
      campaigns.reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Create failed');
    }
  }

  const canCreate = orgId && templateId && senderId && listId;
  const rows = campaigns.data?.data ?? [];

  return (
    <div>
      <PageHeader title="Campaigns" subtitle="Create, launch, and control phishing simulations." />

      <div className="mb-6 flex gap-1 border-b border-line">
        {[
          { key: 'campaigns', label: 'Campaigns' },
          { key: 'config', label: 'Configuration' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() =>
              setParams(t.key === 'campaigns' ? {} : { tab: t.key }, { replace: true })
            }
            className={cx(
              '-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              tab === t.key
                ? 'border-ink text-ink'
                : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'config' ? (
        <ConfigurationSection />
      ) : (
        <>
          {msg && (
            <div className="mb-4 rounded-lg border border-line bg-ink/[0.03] px-3 py-2 text-sm text-ink">
              {msg}
            </div>
          )}

          <Card className="mb-4 p-5">
            <div className="mb-3 text-sm font-semibold text-slate-700">New campaign</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Select
                label="Org"
                value={orgId}
                onChange={setOrgId}
                options={(orgs.data?.data ?? []).map((o) => [o.id, o.name])}
              />
              <Select
                label="Target List"
                value={listId}
                onChange={setListId}
                options={(lists.data?.data ?? []).map((l) => [l.id, l.name])}
              />
              <Select
                label="Template"
                value={templateId}
                onChange={setTemplateId}
                options={(templates.data?.data ?? []).map((t) => [t.id, t.name])}
              />
              <Select
                label="Sender"
                value={senderId}
                onChange={setSenderId}
                options={(senders.data?.data ?? []).map((s) => [s.id, s.display_name])}
              />
            </div>
            <button
              onClick={() => void create()}
              disabled={!canCreate}
              className="btn-primary mt-4"
            >
              <Plus className="h-4 w-4" /> Create campaign
            </button>
          </Card>

          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50/60 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-5 py-3">Campaign</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Recipients</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((c) => (
                  <tr key={c.id} className="transition-colors hover:bg-slate-50/60">
                    <td className="px-5 py-3 font-mono text-xs text-slate-500">{c.id}</td>
                    <td className="px-5 py-3">
                      <Badge tone={statusTone(c.status)}>{c.status}</Badge>
                    </td>
                    <td className="px-5 py-3 tabular-nums text-slate-700">{c.recipient_count}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {c.status === 'draft' && (
                          <button
                            onClick={() => void act(c.id, 'launch')}
                            className="btn-outline px-2.5 py-1.5 text-xs"
                          >
                            <Rocket className="h-3.5 w-3.5" /> Launch
                          </button>
                        )}
                        {(c.status === 'scheduled' || c.status === 'sending') && (
                          <>
                            <button
                              onClick={() => void act(c.id, 'pause')}
                              className="btn-outline px-2.5 py-1.5 text-xs"
                            >
                              <Pause className="h-3.5 w-3.5" /> Pause
                            </button>
                            <button
                              onClick={() => void act(c.id, 'stop')}
                              className="btn px-2.5 py-1.5 text-xs text-ink hover:bg-ink/[0.04]"
                            >
                              <Square className="h-3.5 w-3.5" /> Stop
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && (
              <EmptyState
                icon={Send}
                title="No campaigns yet"
                hint="Create one above to get started."
              />
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input cursor-pointer"
      >
        <option value="">—</option>
        {options.map(([id, name]) => (
          <option key={id} value={id}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}
